import { useQuery, useQueryClient } from "react-query";

import {
  getDesignPreview,
  DesignSelectionApiRequestBody,
} from "../apis/designElementsApi";
import { DESIGN_PREVIEW_QUERY_KEY } from "../queries-keys/designElements";
import useSnackbar from "../components/snackbar/hooks/useSnackbar";
import { getErrorMessage } from "../utils/errors";

interface UseGetDesignPreviewParams extends DesignSelectionApiRequestBody {
  enabled?: boolean;
  onSuccess?: (data: {
    image: string;
    image_url: string | null;
    design_composition: string;
  }) => void;
}

const useGetDesignPreview = ({
  design_composition,
  layout,
  step_info,
  enabled = true,
  onSuccess,
}: UseGetDesignPreviewParams) => {
  const { handleOpenSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    data: previewData,
    isLoading: isGeneratingPreview,
    error: previewError,
    refetch: refetchPreview,
    isFetching: isFetchingPreview,
  } = useQuery(
    DESIGN_PREVIEW_QUERY_KEY(design_composition, layout),
    () =>
      getDesignPreview({
        design_composition,
        layout,
        step_info,
      }),
    {
      enabled: enabled,
      refetchOnMount: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      onError: (error) => {
        handleOpenSnackbar(getErrorMessage(error), "error");
      },
      onSuccess: (data) => {
        if (data.design_composition !== design_composition) {
          const newQueryKey = DESIGN_PREVIEW_QUERY_KEY(
            data.design_composition,
            layout
          );
          queryClient.setQueryData(newQueryKey, data);
        }

        onSuccess?.(data);
      },
    }
  );

  return {
    previewData,
    isGeneratingPreview,
    previewError,
    refetchPreview,
    isFetchingPreview,
    image: previewData?.image || null,
    imageUrl: previewData?.image_url || null,
    designComposition: previewData?.design_composition || null,
    shapes: previewData?.shapes || null,
    materials: previewData?.materials || null,
  };
};

export default useGetDesignPreview;
